import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import { X, Send, CircleCheck } from 'lucide-react';
import { tokens } from '../theme';

const warmFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: `${tokens.radiusControl}px`,
    '& fieldset': {
      borderColor: tokens.border,
    },
    '&:hover fieldset': {
      borderColor: tokens.copper,
    },
    '&.Mui-focused fieldset': {
      borderColor: tokens.copper,
    },
  },
};

interface ContactFormDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  formType?: 'general' | 'enterprise' | 'sales' | 'privacy' | 'support';
  defaultSubject?: string;
  defaultMessage?: string;
}

const ContactFormDialog: React.FC<ContactFormDialogProps> = ({
  open,
  onClose,
  title = 'Contact Us',
  subtitle = "We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
  formType = 'general',
  defaultSubject = '',
  defaultMessage = '',
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: defaultSubject,
    message: defaultMessage,
  });
  React.useEffect(() => {
    if (open) {
      setFormData({ name: '', email: '', subject: defaultSubject, message: defaultMessage });
      setError('');
      setFieldErrors({});
      setSent(false);
    }
  }, [open, defaultSubject, defaultMessage]);

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError('');

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: '' });
    }

    // Validate email on change if it has a value
    if (name === 'email' && value && !validateEmail(value)) {
      setFieldErrors({ ...fieldErrors, email: 'Please enter a valid email address' });
    } else if (name === 'email') {
      setFieldErrors({ ...fieldErrors, email: '' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const errors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSending(true);
    setError('');

    try {
      const response = await fetch('https://cleolead-api-743703226990.us-central1.run.app/api/v1/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          subject: formData.subject.trim(),
          message: formData.message.trim(),
          form_type: formType,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to send message');
      }

      setSending(false);
      setSent(true);

      // Reset after showing success
      setTimeout(() => {
        setSent(false);
        setFormData({ name: '', email: '', subject: '', message: '' });
        onClose();
      }, 3000);
    } catch (err) {
      setSending(false);
      setError(err instanceof Error ? err.message : 'Failed to send message. Please try again.');
    }
  };

  const handleClose = () => {
    if (!sending) {
      setFormData({ name: '', email: '', subject: '', message: '' });
      setError('');
      setFieldErrors({});
      setSent(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: `${tokens.radiusCard}px`,
          bgcolor: 'background.paper',
          border: `1px solid ${tokens.border}`,
          boxShadow: tokens.shadowLift,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography
          variant="h5"
          component="span"
          sx={{ fontFamily: tokens.fontDisplay, fontWeight: 600, letterSpacing: '-0.005em' }}
        >
          {title}
        </Typography>
        <IconButton onClick={handleClose} disabled={sending} sx={{ color: 'text.secondary' }}>
          <X size={20} strokeWidth={1.5} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {sent ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{ color: 'success.main', mb: 2, display: 'inline-flex' }}>
              <CircleCheck size={64} strokeWidth={1.5} />
            </Box>
            <Typography variant="h6" gutterBottom>
              Message Sent!
            </Typography>
            <Typography color="text.secondary">
              Thank you for reaching out. We'll get back to you within 24 hours.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {subtitle}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                name="name"
                label="Your Name"
                fullWidth
                required
                value={formData.name}
                onChange={handleChange}
                sx={{ mb: 2, ...warmFieldSx }}
                disabled={sending}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name}
              />
              <TextField
                name="email"
                label="Email Address"
                type="email"
                fullWidth
                required
                value={formData.email}
                onChange={handleChange}
                sx={{ mb: 2, ...warmFieldSx }}
                disabled={sending}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
              />
              <TextField
                name="subject"
                label="Subject (Optional)"
                fullWidth
                value={formData.subject}
                onChange={handleChange}
                sx={{ mb: 2, ...warmFieldSx }}
                disabled={sending}
              />
              <TextField
                name="message"
                label="Message"
                multiline
                rows={4}
                fullWidth
                required
                value={formData.message}
                onChange={handleChange}
                sx={warmFieldSx}
                disabled={sending}
                placeholder="How can we help you?"
                error={!!fieldErrors.message}
                helperText={fieldErrors.message}
              />
            </Box>
          </>
        )}
      </DialogContent>

      {!sent && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={sending}
            startIcon={
              sending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Send size={18} strokeWidth={1.5} />
              )
            }
          >
            {sending ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ContactFormDialog;
